import styles from './hero-illustration.module.css';

/**
 * Recruitments empty-state hero illustration:
 * Job description → Ideal candidate profile (crowned) → Ranked candidates.
 * Animation is a single choreographed 9s CSS timeline (see hero-illustration.module.css);
 * prefers-reduced-motion users see the static final scene.
 */
function HeroIllustration() {
    const s = styles;
    return (
        <svg
            viewBox="0 0 640 268"
            fill="none"
            fontFamily="'Inter Variable', Inter, system-ui, sans-serif"
            role="img"
            aria-label="A job description becomes an ideal candidate profile, which produces ranked candidates"
            style={{ display: 'block', width: '100%', height: 'auto' }}
        >
            <g className={`${s.a} ${s.scene}`}>
                {/* ── Stage 1 · Job description ── */}
                <g className={`${s.a} ${s.jd} ${s.risebox}`}>
                    <rect x="26" y="44" width="120" height="158" rx="10" fill="#fff" stroke="#c9d4f5" strokeWidth="2" />
                    <text x="40" y="69" fontSize="13" fontWeight="800" letterSpacing="0.4" fill="#3764E7">
                        JD
                    </text>
                    <rect x="40" y="76" width="46" height="6" rx="3" fill="#94a3b8" />
                    <rect x="40" y="94" width="92" height="5.5" rx="2.75" fill="#dbe3f8" />
                    <rect x="40" y="106" width="92" height="5.5" rx="2.75" fill="#dbe3f8" />
                    <rect x="40" y="118" width="68" height="5.5" rx="2.75" fill="#dbe3f8" />
                    <rect x="40" y="134" width="80" height="10" rx="5" fill="#EBF253" />
                    <circle cx="44" cy="158" r="2.5" fill="#3764E7" />
                    <rect x="52" y="155" width="72" height="5.5" rx="2.75" fill="#dbe3f8" />
                    <circle cx="44" cy="171" r="2.5" fill="#3764E7" />
                    <rect x="52" y="168" width="60" height="5.5" rx="2.75" fill="#dbe3f8" />
                    <circle cx="44" cy="184" r="2.5" fill="#3764E7" />
                    <rect x="52" y="181" width="66" height="5.5" rx="2.75" fill="#dbe3f8" />
                </g>
                <text
                    className={`${s.a} ${s.label1}`}
                    x="86"
                    y="232"
                    textAnchor="middle"
                    fontSize="10.5"
                    fontWeight="700"
                    letterSpacing="1.2"
                    fill="#475569"
                >
                    JOB DESCRIPTION
                </text>

                {/* beam 1: JD → profile */}
                <path
                    className={`${s.a} ${s.beam1} ${s.beam}`}
                    pathLength={100}
                    d="M152 122 H222"
                    stroke="#3764E7"
                    strokeWidth="2"
                />
                <path className={`${s.a} ${s.arrow1}`} d="M220 116 L232 122 L220 128 Z" fill="#3764E7" />

                {/* ── Stage 2 · Ideal candidate profile ── */}
                <g className={`${s.a} ${s.profile} ${s.risebox}`}>
                    <rect
                        x="240"
                        y="36"
                        width="140"
                        height="172"
                        rx="12"
                        fill="#fff"
                        stroke="#c9d4f5"
                        strokeWidth="2"
                    />
                    <circle cx="310" cy="74" r="21" fill="#e3eafc" />
                    <circle cx="310" cy="68" r="8" fill="#8fa7e8" />
                    <path d="M296 86 C296 78.5 324 78.5 324 86 L324 89 C324 92 296 92 296 89 Z" fill="#8fa7e8" />
                    <rect x="278" y="102" width="64" height="7" rx="3.5" fill="#334155" />
                    <g className={`${s.a} ${s.chk1} ${s.pop}`}>
                        <circle cx="262" cy="130" r="7" fill="#EBF253" />
                        <path
                            d="M259 130 l2.2 2.4 4-4.6"
                            stroke="#1E2845"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <rect x="276" y="126.5" width="80" height="6" rx="3" fill="#dbe3f8" />
                    </g>
                    <g className={`${s.a} ${s.chk2} ${s.pop}`}>
                        <circle cx="262" cy="152" r="7" fill="#EBF253" />
                        <path
                            d="M259 152 l2.2 2.4 4-4.6"
                            stroke="#1E2845"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <rect x="276" y="148.5" width="66" height="6" rx="3" fill="#dbe3f8" />
                    </g>
                    <g className={`${s.a} ${s.chk3} ${s.pop}`}>
                        <circle cx="262" cy="174" r="7" fill="#EBF253" />
                        <path
                            d="M259 174 l2.2 2.4 4-4.6"
                            stroke="#1E2845"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <rect x="276" y="170.5" width="74" height="6" rx="3" fill="#dbe3f8" />
                    </g>
                    <g className={`${s.a} ${s.crown} ${s.pop}`}>
                        <path
                            d="M300 58 L301.5 47 L306.5 52 L310 45 L313.5 52 L318.5 47 L320 58 Z"
                            fill="#EBF253"
                            stroke="#D4EB47"
                            strokeWidth="1.2"
                            strokeLinejoin="round"
                        />
                        <rect x="300" y="58" width="20" height="3" rx="1.5" fill="#D4EB47" />
                    </g>
                </g>
                <text
                    className={`${s.a} ${s.label2}`}
                    x="310"
                    y="232"
                    textAnchor="middle"
                    fontSize="10.5"
                    fontWeight="700"
                    letterSpacing="1.2"
                    fill="#475569"
                >
                    IDEAL CANDIDATE PROFILE
                </text>

                {/* beams 2-4: profile → candidates */}
                <path
                    className={`${s.a} ${s.beam2} ${s.beam}`}
                    pathLength={100}
                    d="M386 90 C420 76 436 68 462 58"
                    stroke="#3764E7"
                    strokeWidth="2"
                />
                <path className={`${s.a} ${s.arrow2}`} d="M459 51.5 L472 54.5 L464 63 Z" fill="#3764E7" />
                <path
                    className={`${s.a} ${s.beam3} ${s.beam}`}
                    pathLength={100}
                    d="M386 122 H460"
                    stroke="#3764E7"
                    strokeWidth="2"
                />
                <path className={`${s.a} ${s.arrow3}`} d="M458 116 L470 122 L458 128 Z" fill="#3764E7" />
                <path
                    className={`${s.a} ${s.beam4} ${s.beam}`}
                    pathLength={100}
                    d="M386 154 C420 168 436 176 462 186"
                    stroke="#3764E7"
                    strokeWidth="2"
                />
                <path className={`${s.a} ${s.arrow4}`} d="M464 180.5 L472 189.5 L459 192 Z" fill="#3764E7" />

                {/* ── Stage 3 · Ranked candidates ── */}
                <g className={`${s.a} ${s.card1} ${s.risebox}`}>
                    <rect x="478" y="26" width="148" height="50" rx="10" fill="#fff" stroke="#c9d4f5" strokeWidth="2" />
                    <g>
                        <clipPath id="av1">
                            <circle cx="504" cy="51" r="15" />
                        </clipPath>
                        <circle cx="504" cy="51" r="15" fill="#fde8cf" />
                        <g clipPath="url(#av1)">
                            <rect x="500.5" y="50" width="7" height="8" rx="2" fill="#f2c29b" />
                            <path d="M491 66 C491 56.5 517 56.5 517 66 L517 68 L491 68 Z" fill="#3764E7" />
                            <circle cx="504" cy="47.5" r="7" fill="#f2c29b" />
                            <path d="M497 46 A7 7 0 0 1 511 46 L511 47.5 L497 47.5 Z" fill="#4a3428" />
                        </g>
                        <circle cx="504" cy="51" r="15" stroke="#c9d4f5" strokeWidth="1.5" />
                    </g>
                    <rect x="526" y="39" width="58" height="7" rx="3.5" fill="#334155" />
                    <rect x="526" y="53" width="42" height="6" rx="3" fill="#dbe3f8" />
                    <rect x="592" y="42" width="26" height="14" rx="7" fill="#EBF253" />
                    <text x="605" y="52" textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#1E2845">
                        98
                    </text>
                </g>
                <g className={`${s.a} ${s.card2} ${s.risebox}`}>
                    <rect x="486" y="90" width="148" height="50" rx="10" fill="#fff" stroke="#c9d4f5" strokeWidth="2" />
                    <g>
                        <clipPath id="av2">
                            <circle cx="512" cy="115" r="15" />
                        </clipPath>
                        <circle cx="512" cy="115" r="15" fill="#e3eafc" />
                        <g clipPath="url(#av2)">
                            <rect x="508.5" y="114" width="7" height="8" rx="2" fill="#a5713f" />
                            <path d="M499 130 C499 120.5 525 120.5 525 130 L525 132 L499 132 Z" fill="#1E2845" />
                            <circle cx="512" cy="111.5" r="7" fill="#a5713f" />
                            <path d="M505 110 A7 7 0 0 1 519 110 L519 111.5 L505 111.5 Z" fill="#191210" />
                        </g>
                        <circle cx="512" cy="115" r="15" stroke="#c9d4f5" strokeWidth="1.5" />
                    </g>
                    <rect x="534" y="103" width="58" height="7" rx="3.5" fill="#334155" />
                    <rect x="534" y="117" width="42" height="6" rx="3" fill="#dbe3f8" />
                    <rect x="600" y="106" width="26" height="14" rx="7" fill="#EBF253" />
                    <text x="613" y="116" textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#1E2845">
                        95
                    </text>
                </g>
                <g className={`${s.a} ${s.card3} ${s.risebox}`}>
                    <rect
                        x="478"
                        y="154"
                        width="148"
                        height="50"
                        rx="10"
                        fill="#fff"
                        stroke="#c9d4f5"
                        strokeWidth="2"
                    />
                    <g>
                        <clipPath id="av3">
                            <circle cx="504" cy="179" r="15" />
                        </clipPath>
                        <circle cx="504" cy="179" r="15" fill="#fbf6d8" />
                        <g clipPath="url(#av3)">
                            <path
                                d="M495 190 L495 176 C495 165 513 165 513 176 L513 190 Q513 192 511 192 L497 192 Q495 192 495 190 Z"
                                fill="#c9942f"
                            />
                            <rect x="500.5" y="178" width="7" height="8" rx="2" fill="#efc0a0" />
                            <path d="M491 194 C491 184.5 517 184.5 517 194 L517 196 L491 196 Z" fill="#8fa7e8" />
                            <circle cx="504" cy="175.5" r="7" fill="#efc0a0" />
                            <path d="M497 174 A7 7 0 0 1 511 174 L511 175.5 L497 175.5 Z" fill="#c9942f" />
                        </g>
                        <circle cx="504" cy="179" r="15" stroke="#c9d4f5" strokeWidth="1.5" />
                    </g>
                    <rect x="526" y="167" width="58" height="7" rx="3.5" fill="#334155" />
                    <rect x="526" y="181" width="42" height="6" rx="3" fill="#dbe3f8" />
                    <rect x="592" y="170" width="26" height="14" rx="7" fill="#EBF253" />
                    <text x="605" y="180" textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#1E2845">
                        93
                    </text>
                </g>
                <text
                    className={`${s.a} ${s.label3}`}
                    x="556"
                    y="232"
                    textAnchor="middle"
                    fontSize="10.5"
                    fontWeight="700"
                    letterSpacing="1.2"
                    fill="#475569"
                >
                    RANKED CANDIDATES
                </text>
            </g>
        </svg>
    );
}

export default HeroIllustration;
